import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { ageFromBirth, isAdult } from "../lib/calc";
import { ArrowLeft, Activity, AlertTriangle, CheckCircle2, Eye, Calendar, Droplet, ShieldAlert, Trash2, FileDown, Printer, FolderOpen, FileText, FileCode2, QrCode, Loader2, Stethoscope, LineChart, Pill, FileSignature, FlaskConical, Syringe, Baby, Sparkles } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import GrowthChart from "../components/GrowthChart";
import NewConsultationForm from "../components/patient/NewConsultationForm";
import PrescriptionPanel from "../components/patient/PrescriptionPanel";
import AIPanel from "../components/patient/AIPanel";
import MilestonesPanel from "../components/patient/MilestonesPanel";
import VaccinesTab from "../components/patient/VaccinesTab";
import AttachmentsPanel from "../components/patient/AttachmentsPanel";
import AppointmentsPanel from "../components/patient/AppointmentsPanel";
import ShareQRDialog from "../components/ShareQRDialog";
import { Nom004Badge } from "../components/Nom004Badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { downloadExpedientePDF, openConsultationPDF } from "../lib/pdf";
import { correctedAgeMonths } from "../lib/calc";
import { toast } from "sonner";

const STATUS = {
  alarma: { color: "text-destructive bg-destructive/10", icon: ShieldAlert, label: "Fuera de rango", pulse: true },
  vigilancia: { color: "text-amber-600 bg-amber-50", icon: AlertTriangle, label: "Vigilancia", pulse: false },
  normal: { color: "text-brand-sageDark bg-brand-sage/15", icon: CheckCircle2, label: "Normal", pulse: false },
};

export default function PatientDetail() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("expediente");
  const [rxPrefill, setRxPrefill] = useState(null);
  const [finalizeSignal, setFinalizeSignal] = useState(0);
  const [finalizing, setFinalizing] = useState(false);
  const [shareDoc, setShareDoc] = useState(null);
  const [sharingExp, setSharingExp] = useState(false);
  const [studyNote, setStudyNote] = useState(null);
  const [versionsFor, setVersionsFor] = useState(null);
  const [versions, setVersions] = useState([]);

  const audit = (action, entity, entity_id, detail = "") =>
    api.post("/audit/log", { action, entity, entity_id, patient_id: patientId, detail }).catch(() => {});

  const openVersions = async (c) => {
    setVersionsFor(c);
    setVersions([]);
    try {
      const r = await api.get(`/consultations/${c.consultation_id}/versions`);
      setVersions(r.data);
    } catch { /* noop */ }
  };

  const handleSendToConsult = (text) => { setStudyNote({ text, _ts: Date.now() }); setTab("consultas"); };

  const shareExpediente = async () => {
    setSharingExp(true);
    try {
      const finals = consultations.filter((c) => c.status !== "en_progreso");
      const demo = [
        patient.curp && `CURP: ${patient.curp}`,
        patient.nationality && `Nacionalidad: ${patient.nationality}`,
        patient.blood_type && `Tipo de sangre: ${patient.blood_type}`,
        patient.allergies && `Alergias: ${patient.allergies}`,
      ].filter(Boolean);
      const consItems = finals.map((c) => {
        const d = (() => { try { return new Date(c.date).toLocaleDateString("es-MX"); } catch { return ""; } })();
        return `${d} — ${c.diagnosis || "Sin diagnóstico"}${c.treatment ? ` · Tx: ${c.treatment}` : ""}`;
      });
      const res = await api.post("/prescriptions", {
        patient_id: patientId, kind: "expediente", title: "Resumen de expediente clínico",
        sections: [
          { heading: "Datos del paciente", items: demo.length ? demo : ["—"] },
          { heading: `Consultas (${consItems.length})`, items: consItems.length ? consItems : ["Sin consultas finalizadas"] },
        ],
      });
      setShareDoc(res.data);
    } catch { toast.error("No se pudo generar el QR del expediente"); }
    finally { setSharingExp(false); }
  };
  const triggerFinalize = () => { setFinalizing(true); setFinalizeSignal((s) => s + 1); };

  const loadAll = useCallback(async () => {
    try {
      const [p, c, rx] = await Promise.all([
        api.get(`/patients/${patientId}`),
        api.get(`/patients/${patientId}/consultations`),
        api.get(`/patients/${patientId}/prescriptions`),
      ]);
      setPatient(p.data); setConsultations(c.data); setPrescriptions(rx.data);
    } catch (e) { toast.error("No se pudo cargar el paciente"); }
    finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);

  // Mantiene la pestaña activa en la URL para que ClinPi muestre la referencia correcta.
  useEffect(() => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (p.get("tab") !== tab) p.set("tab", tab);
      return p;
    }, { replace: true });
  }, [tab]); // eslint-disable-line

  const refreshConsults = () => api.get(`/patients/${patientId}/consultations`).then((r) => setConsultations(r.data));
  const refreshRx = () => api.get(`/patients/${patientId}/prescriptions`).then((r) => setPrescriptions(r.data));
  const handleSendToRx = (data) => { setRxPrefill({ ...data, _ts: Date.now() }); setTab("recetas"); };

  const remove = async () => {
    if (!window.confirm("¿Eliminar este expediente y sus consultas?")) return;
    await api.delete(`/patients/${patientId}`);
    toast.success("Expediente eliminado");
    navigate("/pacientes");
  };

  const exportCDA = async () => {
    try {
      const res = await api.get(`/patients/${patientId}/cda`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = `expediente_${patient.record_number || patientId}.xml`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Expediente exportado (HL7 CDA / XML)");
    } catch { toast.error("No se pudo exportar el XML"); }
  };

  if (loading) return <div className="py-20 flex justify-center"><div className="w-9 h-9 rounded-full border-[3px] border-brand-sage/30 border-t-brand-terracotta animate-spin" /></div>;
  if (!patient) return null;

  const last = consultations[consultations.length - 1];
  const finalizedConsults = consultations.filter((c) => c.status !== "en_progreso");
  const evals = last?.somatometry_eval || [];
  const latestDiagnosis = last?.diagnosis || "";
  const adult = isAdult(patient.birth_date);

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/pacientes")} className="flex items-center gap-1.5 text-muted-foreground hover:text-brand-ink text-sm font-semibold">
        <ArrowLeft size={16} /> Pacientes
      </button>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)]">
        <div className="flex flex-wrap items-start gap-4 justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-3xl flex items-center justify-center font-outfit font-extrabold text-white text-2xl ${patient.sex === "F" ? "bg-brand-terracotta" : "bg-brand-sage"}`}>
              {patient.name?.[0]}
            </div>
            <div>
              <h1 className="font-outfit text-2xl font-extrabold text-brand-ink" data-testid="patient-name">{patient.name}</h1>
              <p className="text-muted-foreground text-sm">{patient.record_number} · {ageFromBirth(patient.birth_date).text} · {patient.sex === "F" ? "Femenino" : "Masculino"}{!adult && patient.gestational_weeks < 37 ? ` · ${patient.gestational_weeks} SDG (corregida ${correctedAgeMonths(patient.birth_date, patient.gestational_weeks)}m)` : ""}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {adult && <span className="text-xs bg-brand-ink/10 text-brand-ink px-2.5 py-1 rounded-full font-semibold" data-testid="adult-badge">Adulto</span>}
                {patient.blood_type && <span className="text-xs bg-muted px-2.5 py-1 rounded-full font-semibold flex items-center gap-1"><Droplet size={12} />{patient.blood_type}</span>}
                {patient.allergies && <span className="text-xs bg-destructive/10 text-destructive px-2.5 py-1 rounded-full font-semibold">Alergias: {patient.allergies}</span>}
                {patient.guardian_name && <span className="text-xs bg-muted px-2.5 py-1 rounded-full font-semibold">Tutor: {patient.guardian_name}</span>}
                {patient.curp && <span data-testid="patient-curp" className="text-xs bg-brand-sage/10 text-brand-sageDark px-2.5 py-1 rounded-full font-semibold">CURP: {patient.curp}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="export-expediente" onClick={() => { audit("exportar", "expediente", patientId, "Resumen PDF"); downloadExpedientePDF({ patient, doctor: user, consultations, prescriptions }); }}
              className="flex items-center gap-1.5 bg-brand-ink text-white text-sm font-semibold px-3.5 py-2 rounded-xl hover:opacity-90 transition-all">
              <FileDown size={16} /> Resumen PDF
            </button>
            <button data-testid="print-expediente" onClick={() => { audit("imprimir", "expediente", patientId); downloadExpedientePDF({ patient, doctor: user, consultations, prescriptions, openTab: true }); }}
              className="flex items-center gap-1.5 bg-white border border-stone-200 text-brand-ink text-sm font-semibold px-3.5 py-2 rounded-xl hover:bg-muted transition-all" title="Abrir / Imprimir">
              <Printer size={16} /> Imprimir
            </button>
            <button data-testid="export-cda" onClick={exportCDA}
              className="flex items-center gap-1.5 bg-white border border-stone-200 text-brand-ink text-sm font-semibold px-3.5 py-2 rounded-xl hover:bg-muted transition-all" title="Exportar en HL7 CDA / XML (NOM-024)">
              <FileCode2 size={16} /> XML (CDA)
            </button>
            <button data-testid="share-expediente" onClick={shareExpediente} disabled={sharingExp}
              className="flex items-center gap-1.5 bg-brand-sage text-white text-sm font-semibold px-3.5 py-2 rounded-xl hover:opacity-90 transition-all disabled:opacity-60" title="Compartir resumen por QR">
              {sharingExp ? <Loader2 size={16} className="animate-spin" /> : <QrCode size={16} />} QR
            </button>
            <button data-testid="delete-patient" onClick={remove} className="p-2 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"><Trash2 size={18} /></button>
          </div>
        </div>

        {/* Alarms / latest somatometry (pediátrico) */}
        {!adult && evals.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            {evals.map((e) => {
              const st = STATUS[e.status] || STATUS.normal;
              const Icon = st.icon;
              return (
                <div key={e.metric} data-testid={`soma-${e.metric}`}
                  className={`rounded-2xl p-3 ${st.color} ${st.pulse ? "animate-pulse-alarm" : ""}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wide opacity-80">{e.label.split(" ")[0]}</p>
                  <p className="font-outfit text-xl font-extrabold mt-0.5">{e.value} <span className="text-xs">{e.unit}</span></p>
                  <p className="text-xs font-semibold flex items-center gap-1 mt-1"><Icon size={12} />P{Math.round(e.percentile)} · {st.label}</p>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white rounded-2xl p-1.5 flex-wrap h-auto gap-1 border border-stone-100">
          {[
            ["expediente", "Expediente", FolderOpen], ["consultas", "Historia clínica", Stethoscope],
            ...(adult ? [] : [["graficas", "Gráficas", LineChart]]),
            ["recetas", "Recetas/Estudios", Pill], ["consentimiento", "Consentimiento", FileSignature], ["archivos", "Laboratorios", FlaskConical],
            ...(adult ? [] : [["vacunas", "Vacunas", Syringe], ["hitos", "Hitos", Baby]]),
            ["citas", "Citas", Calendar], ["ia", "Asistente IA", Sparkles],
          ].map(([v, l, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
              className="rounded-xl data-[state=active]:bg-brand-terracotta data-[state=active]:text-white font-semibold text-sm px-3.5 flex items-center gap-1.5">
              <Icon size={15} className="opacity-90" /> {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="expediente" className="mt-5">
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="font-outfit font-bold text-lg text-brand-ink flex items-center gap-2"><FolderOpen size={18} className="text-brand-sage" /> Carpeta del paciente</h3>
              <button data-testid="start-consult-from-folder" onClick={() => setTab("consultas")}
                className="flex items-center gap-1.5 bg-brand-terracotta text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-brand-terracottaDark transition-all">
                <Activity size={16} /> {finalizedConsults.length ? "Nueva consulta" : "Iniciar consulta"}
              </button>
            </div>
            <p className="text-sm text-muted-foreground">Consultas guardadas (la más reciente primero). Toca "Abrir PDF" para revisar una consulta.</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="folder-shortcuts">
              {[
                ["Receta / Estudio", Pill, "recetas", "text-brand-terracotta bg-brand-terracotta/10"],
                ["Agendar cita", Calendar, "citas", "text-brand-sageDark bg-brand-sage/15"],
                ...(adult ? [["Laboratorios", FlaskConical, "archivos", "text-amber-700 bg-amber-50"]] : [["Gráficas", LineChart, "graficas", "text-teal-700 bg-teal-50"]]),
                ["Asistente IA", Sparkles, "ia", "text-violet-700 bg-violet-50"],
              ].map(([label, Icon, target, cls]) => (
                <button key={target} data-testid={`shortcut-${target}`} onClick={() => setTab(target)}
                  className="flex items-center gap-2.5 bg-white border border-stone-100 rounded-2xl px-3.5 py-3 text-left hover:border-brand-terracotta/30 hover:shadow-sm transition-all active:scale-[0.98]">
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${cls}`}><Icon size={17} /></span>
                  <span className="text-sm font-semibold text-brand-ink leading-tight">{label}</span>
                </button>
              ))}
            </div>
            {finalizedConsults.length === 0 ? (
              <div className="bg-white rounded-3xl p-8 text-center border border-stone-100 text-muted-foreground text-sm" data-testid="no-prev-consults">Carpeta vacía: aún no hay consultas finalizadas. Inicia la primera consulta.</div>
            ) : [...finalizedConsults].reverse().map((c) => (
              <motion.div key={c.consultation_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-4 border border-stone-100" data-testid={`consult-${c.consultation_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-outfit font-semibold text-brand-ink text-sm flex items-center gap-1.5"><Calendar size={14} className="text-brand-sage" />{new Date(c.date).toLocaleDateString("es-MX")}</p>
                  <div className="flex items-center gap-2">
                    {c.alerts?.length > 0 && <span className="text-xs bg-destructive/10 text-destructive font-semibold px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle size={11} />{c.alerts.length}</span>}
                    <Nom004Badge nom004={c.nom004} consultation={c} compact testid={`nom004-${c.consultation_id}`} />
                    {c.edited_after_finalize && (
                      <button data-testid={`versions-${c.consultation_id}`} onClick={() => openVersions(c)}
                        className="flex items-center gap-1 text-xs font-semibold text-brand-sageDark bg-brand-sage/10 hover:bg-brand-sage/20 px-2 py-1 rounded-lg transition-all">
                        <FileCode2 size={13} /> Versiones
                      </button>
                    )}
                    <button data-testid={`open-consult-pdf-${c.consultation_id}`}
                      onClick={() => { audit("abrir", "consulta", c.consultation_id); openConsultationPDF({ patient, doctor: user, consultation: c }); }}
                      className="flex items-center gap-1.5 text-brand-terracotta hover:bg-brand-terracotta/10 text-xs font-bold px-2.5 py-1.5 rounded-lg transition-all">
                      <FileText size={14} /> Abrir PDF
                    </button>
                  </div>
                </div>
                {(c.current_illness || c.reason) && <p className="text-sm text-brand-ink/80 mt-1"><b>Padecimiento:</b> {c.current_illness || c.reason}</p>}
                {c.diagnosis && <p className="text-sm text-brand-ink/80"><b>Dx:</b> {c.diagnosis}</p>}
                <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                  {c.weight && <span>Peso {c.weight}kg</span>}{c.height && <span>· Talla {c.height}cm</span>}{c.bmi && <span>· IMC {c.bmi}</span>}{c.temperature && <span>· T {c.temperature}°C</span>}
                </div>
              </motion.div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="graficas" className="mt-5">
          {!adult && <GrowthChart patientId={patientId} sex={patient.sex} ageMonths={correctedAgeMonths(patient.birth_date, patient.gestational_weeks)} />}
        </TabsContent>

        <TabsContent value="consultas" className="mt-5" forceMount hidden={tab !== "consultas"}>
          <NewConsultationForm patient={patient} adult={adult} consultations={consultations} onSaved={() => { refreshConsults(); loadAll(); }} onSendToRx={handleSendToRx} finalizeSignal={finalizeSignal} onFinalizeDone={() => setFinalizing(false)} studyNote={studyNote} />
        </TabsContent>

        <TabsContent value="recetas" className="mt-5" forceMount hidden={tab !== "recetas"}>
          <PrescriptionPanel patient={patient} pediatric={!adult} prescriptions={prescriptions} onChange={refreshRx} prefill={rxPrefill} latestWeight={last?.weight} ageMonths={ageFromBirth(patient.birth_date).months} only={["receta", "estudio"]} />
        </TabsContent>

        <TabsContent value="consentimiento" className="mt-5" forceMount hidden={tab !== "consentimiento"}>
          <PrescriptionPanel patient={patient} pediatric={!adult} prescriptions={prescriptions} onChange={refreshRx} latestWeight={last?.weight} ageMonths={ageFromBirth(patient.birth_date).months} only={["consentimiento"]} />
        </TabsContent>

        {!adult && (
          <>
            <TabsContent value="hitos" className="mt-5">
              <MilestonesPanel patient={patient} />
            </TabsContent>

            <TabsContent value="vacunas" className="mt-5">
              <VaccinesTab patient={patient} />
            </TabsContent>
          </>
        )}

        <TabsContent value="archivos" className="mt-5">
          <AttachmentsPanel patient={patient} onSendToConsult={handleSendToConsult} />
        </TabsContent>

        <TabsContent value="citas" className="mt-5">
          <AppointmentsPanel patient={patient} />
        </TabsContent>

        <TabsContent value="ia" className="mt-5">
          <AIPanel patient={patient} latestDiagnosis={latestDiagnosis} onFinalize={triggerFinalize} finalizing={finalizing} onSendToRx={handleSendToRx} />
        </TabsContent>
      </Tabs>
      <ShareQRDialog doc={shareDoc} onClose={() => setShareDoc(null)} />

      <Dialog open={!!versionsFor} onOpenChange={(o) => !o && setVersionsFor(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="versions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileCode2 size={18} className="text-brand-sage" /> Historial de versiones (NOM-004)</DialogTitle>
            <DialogDescription>Versiones previas guardadas cada vez que se edita una consulta finalizada.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Cada edición de una consulta finalizada guarda la versión previa. No hay sobrescritura silenciosa.</p>
          {versions.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No hay versiones previas guardadas.</div>
          ) : (
            <div className="space-y-3 mt-1">
              {versions.map((v) => (
                <div key={v.version_id} className="bg-muted/40 border border-stone-200 rounded-2xl p-3.5" data-testid={`version-${v.version}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                    <span className="text-xs font-bold text-brand-ink">Versión {v.version}</span>
                    <span className="text-[11px] text-muted-foreground">{new Date(v.at).toLocaleString("es-MX")}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-2">Editado por <b>{v.edited_by}</b> · IP {v.ip || "—"} · campos: {(v.changed_fields || []).join(", ")}</p>
                  {v.diagnosis && <p className="text-xs text-brand-ink/80"><b>Dx:</b> {v.diagnosis}</p>}
                  {v.treatment && <p className="text-xs text-brand-ink/80"><b>Tx:</b> {v.treatment}</p>}
                  {v.current_illness && <p className="text-xs text-brand-ink/80"><b>Padecimiento:</b> {v.current_illness}</p>}
                  {v.physical_exam && <p className="text-xs text-brand-ink/80"><b>Exploración:</b> {v.physical_exam}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
